import { Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DocumentService } from 'src/app/services/document/document.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { LoginService } from 'src/app/services/login/login.service';
import { SnackbarService } from 'src/app/services/snackbar/snackbar.service';
import { ImportDocumentsComponent } from 'src/app/fragments/import-documents/import-documents.component';
import { UserDocument, UserDocumentsService } from 'src/app/services/user-documents/user-documents.service';
import { DisplayDocumentService } from 'src/app/services/display-document/display-document.service';
import { DisplayContentData } from 'src/app/fragments/display-document/gedviewer/gedviewer.component';
import moment from 'moment';

interface UserDocumentDisplay {
  id: string;
  title: string;
  formatFileSize: string;
  createdAt: string;
  fileName: string;
  contentType: string;
}

@Component({
  selector: 'app-user-documents',
  templateUrl: './user-documents.component.html',
  styleUrls: ['./user-documents.component.scss'],
})
export class UserDocumentsComponent implements OnInit {
  @ViewChild('importDocumentsComponent')importDocumentsComponent: ImportDocumentsComponent;
  @ViewChild(MatPaginator)paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  public formGroup: FormGroup;
  public documentsTable: Paginated<UserDocument>;
  public dataSource = new MatTableDataSource<UserDocumentDisplay>();
  public displayedColumns: string[] = [
    'title',
    'formatFileSize',
    'createdAt',
    'actions',
  ];
  public isInitOver = false;
  private _tableFiltre: Filtres = {
    currentPage: 1,
    perpage: 10,
    sortBy: 'createdAt',
  };
  private _userId: string;
  public pageTitle = 'Documents utilisateur';

  constructor(
    private _formBuilder: FormBuilder,
    private _userDocumentSrv: UserDocumentsService,
    private _modalSrv: ModalService,
    private _viewContainerRef: ViewContainerRef,
    private _loginSrv: LoginService,
    private _snackbar: SnackbarService,
    private _displayDocumentSrv: DisplayDocumentService
  ) {}

  async ngOnInit(): Promise<void> {
    await this._initData();
  }

  private async _initData(): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);
    try {
      // Récupérer l'ID de l'utilisateur connecté
      const currentProfil = this._loginSrv.getCurrentProfil();
      this._userId = currentProfil?.id;
      this._initForm();
      // Charger les documents
      await this.searchDocuments();
      this.isInitOver = true;
    } catch (e) {
      console.error(
        "Une erreur s'est produite lors de l'initialisation de user-documents",
        e
      );
      this._snackbar.openSnackBarError(
        "Une erreur s'est produite lors du chargement de cette page."
      );
    } finally {
      this._modalSrv.close(loading);
    }
  }

  private _initForm(): void {
    this.formGroup = this._formBuilder.group({
      plainText: [''],
    });
  }

  /**
   * Recherche les documents utilisateur
   */
  public async searchDocuments(resetPage: boolean = false): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);
    try {
      if (resetPage) {
        this._tableFiltre.currentPage = 1;
      }
      const title = this.formGroup.get('plainText').value;

      const documents = await this._getDocuments(
        title && title !== '' ? title : undefined
      );

      // Ajouter les documents au tableau de résultat
      this.documentsTable = documents;

      // Convertir et mettre à jour le dataSource
      this._updateDataSource(documents.objects);
    } catch (e) {
      console.error(
        "Une erreur s'est produite lors de la recherche de documents.",
        e
      );
      this._snackbar.openSnackBarError(
        "Une erreur s'est produite lors de la recherche de documents."
      );
    } finally {
      this._modalSrv.close(loading);
    }
  }

  private async _getDocuments(
    title?: string
  ): Promise<Paginated<UserDocument>> {
    return new Promise<Paginated<UserDocument>>(async (resolve, reject) => {
      try {
        const rtd = await this._userDocumentSrv.findUserDocuments({
          title,
          page: this._tableFiltre.currentPage,
          perPage: this._tableFiltre.perpage
        });
        resolve(rtd);
      } catch (e) {
        reject(e);
      }
    });
  }

  private _updateDataSource(documents: UserDocument[]): void {
    const displayDocuments: UserDocumentDisplay[] = documents.map((doc) => ({
      id: doc.id,
      title: doc.title,
      formatFileSize: this._getFormatFileSize(doc.weight),
      createdAt: moment(doc.createdAt).format('DD/MM/YYYY à HH:mm'),
      fileName: doc.fileName,
      contentType: doc.contentType,
    }));

    this.dataSource.data = displayDocuments;

    // Configure le sort après la mise à jour des données
    setTimeout(() => {
      if (this.sort) {
        this.dataSource.sort = this.sort;
      }
    });
  }

  private _getFormatFileSize(fileSize: number): string {
    const fileSizeString = fileSize.toString();
    let result: string;
    switch (true) {
      case fileSizeString.length > 0 && fileSizeString.length < 4:
        result = fileSizeString + ' octets';
        break;
      case fileSizeString.length >= 4 && fileSizeString.length < 7:
        result = Math.round((fileSize / 1000) * 10) / 10 + ' ko';
        break;
      case fileSizeString.length >= 7 && fileSizeString.length < 10:
        result = Math.round((fileSize / 1000000) * 10) / 10 + ' Mo';
        break;
      case fileSizeString.length >= 10 && fileSizeString.length < 13:
        result = Math.round((fileSize / 1000000000) * 10) / 10 + ' Go';
        break;
      case fileSizeString.length >= 13:
        result = Math.round((fileSize / 1000000000000) * 10) / 10 + ' To';
        break;
      default:
        result = 'NA';
        break;
    }
    result = result.replace('.', ',');
    return result;
  }

  public async changePage(event: any): Promise<void> {
    this._tableFiltre.currentPage = event.pageIndex + 1;
    this._tableFiltre.perpage = event.pageSize;
    await this.searchDocuments();
  }

  public async sortDocuments(sort: any): Promise<void> {
    this._tableFiltre.currentPage = 1;
    this._tableFiltre.sortBy = sort.sortBy;
    this._tableFiltre.sortOrder = sort.sortOrder;
    await this.searchDocuments();
  }

  public async downloadDocument(doc: UserDocumentDisplay): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);
    try {
      const fileData = await this._userDocumentSrv.downloadUserDocument(doc.id);

      // Créer un lien de téléchargement
      const link = document.createElement('a');
      link.href = `data:${fileData.contentType};base64,${fileData.payload}`;
      link.download = fileData.fileName;
      link.click();

      this._snackbar.openSnackBar('Document téléchargé avec succès');
    } catch (e) {
      console.error(
        "Une erreur s'est produite lors du téléchargement du document",
        e
      );
      this._snackbar.openSnackBarError(
        'Erreur lors du téléchargement du document'
      );
    } finally {
      this._modalSrv.close(loading);
    }
  }

  public async renameDocument(doc: UserDocumentDisplay): Promise<void> {
    const modal = this._modalSrv.openModalAddEdit(
      'Renommer le document',
      this._viewContainerRef,
      {
        inputs: [
          {
            id: 'new_name',
            label: 'Nouveau titre',
            placeholder: '',
            type: 'text',
            variable: 'new_name',
            required: true,
            style: { width: '300px' },
          },
        ],
      },
      true,
      { new_name: doc.title }
    );

    const sub = this._modalSrv.closeEmitter.subscribe(async (v: any) => {
      const value = v?.object;
      if (value && value.new_name) {
        const loading = this._modalSrv.openLoading(this._viewContainerRef);
        try {
          const userDoc = this.documentsTable.objects.find(
            (d) => d.id === doc.id
          );
          if (userDoc) {
            userDoc.title = value.new_name;
            await this._userDocumentSrv.updateUserDocument(doc.id, userDoc);
            this._snackbar.openSnackBar('Document renommé avec succès');
            await this.searchDocuments();
          }
        } catch (e) {
          console.error(
            "Une erreur s'est produite lors du renommage du document",
            e
          );
          this._snackbar.openSnackBarError(
            'Erreur lors du renommage du document'
          );
        } finally {
          this._modalSrv.close(loading);
        }
      }

      if (v?.loadingModal) {
        v.loadingModal.destroy();
      }

      sub.unsubscribe();
      modal.destroy();
    });
  }

  public async deleteDocument(doc: UserDocumentDisplay): Promise<void> {
    const modal = this._modalSrv.openModalConfirmation(
      this._viewContainerRef,
      'Confirmation',
      'Voulez-vous vraiment supprimer ce document ?'
    );

    modal.instance.closeEmitter.subscribe(async (res: boolean) => {
      if (res) {
        const loading = this._modalSrv.openLoading(this._viewContainerRef);
        try {
          await this._userDocumentSrv.deleteUserDocument(doc.id);
          this._snackbar.openSnackBar('Document supprimé avec succès');
          await this.searchDocuments();
        } catch (e) {
          console.error(
            "Une erreur s'est produite lors de la suppression du document",
            e
          );
          this._snackbar.openSnackBarError(
            'Erreur lors de la suppression du document'
          );
        } finally {
          this._modalSrv.close(loading);
        }
      }
    });
  }

  public async openDocument(doc: UserDocumentDisplay): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);
    try {
      const fileData = await this._userDocumentSrv.downloadUserDocument(doc.id);

      const displayData: DisplayContentData = {
        id: doc.id,
        title: doc.title,
        fileName: doc.fileName,
        contentType: doc.contentType,
        date: new Date(),
        data: DocumentService.prefixBase64(
          fileData.payload,
          fileData.contentType
        ),
      };

      this._modalSrv.close(loading);

      await this._displayDocumentSrv.displayDocument(
        this._viewContainerRef,
        displayData
      );
    } catch (e) {
      console.error(
        "Une erreur s'est produite lors de l'ouverture du document",
        e
      );
      this._snackbar.openSnackBarError(
        "Erreur lors de l'ouverture du document"
      );
      this._modalSrv.close(loading);
    }
  }

  public async importOverFnc(): Promise<void> {
    await this.searchDocuments();
  }
}
